import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  filterBy,
  CompositeFilterDescriptor,
} from "@progress/kendo-data-query";
import {
  Grid as KendoGrid,
  GridColumn as Column,
  GridCellProps,
  GridFilterChangeEvent,
  GridPageChangeEvent,
  GridCustomHeaderCellProps,
  GridCustomCellProps,
} from "@progress/kendo-react-grid";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { PagerTargetEvent } from "@progress/kendo-react-data-tools";
import { HeaderThElement } from "@progress/kendo-react-data-tools";
import { Typography } from "@progress/kendo-react-common";
import { Button } from "@progress/kendo-react-buttons";
import { FiEdit } from "react-icons/fi";
import { Switch } from "@progress/kendo-react-inputs";
import AlertBox from "../../components/common/AlertBox";
import { LoadingPanel } from "../../components/layout/Loading";
import IconButton from "../../components/common/IconButton";
import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import { MdDelete } from "react-icons/md";
import {
  ActiveInactiveItinerary,
  bulkUploadItineraries,
  deleteItinerary,
  getAllItinerary,
} from "./services/itineary.services";
import { clearItinearyID, setItinearyID } from "./itinearySlice";

import { checkAcessRights } from "../../_helper/helper";
import {
  DELETE_ACCESS,
  EDIT_ACCESS,
  INSERT_ACCESS,
} from "../../_contstants/common";
import { Field, Form, FormElement } from "@progress/kendo-react-form";
import { RxCross1 } from "react-icons/rx";
import FormFileUpload from "../../components/formFields/FormFileUpload";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import itinerarySample from "../../assets/Excels/itinerary_sample.xlsx";

const Itinerary: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isAddAccess = checkAcessRights(location.pathname, INSERT_ACCESS);

  useEffect(() => {
    dispatch(getAllItinerary());
    return () => {
      dispatch(closeDialog());
    };
  }, []);

  const handleCreate = () => {
    navigate("/itineary/create");
  };

  const handleOpenBulkUploadDialog = () => {
    dispatch(openDialog("bulkUploadItinerary"));
  };

  return (
    <>
      <DeleteItineraryDialog />
      <BulkUploadItineraryDialog />
      <GridLayout cols={[{ width: "100%" }]}>
        <GridLayoutItem
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 0px 20px",
          }}
        >
          <Typography.h4 style={{ marginBottom: 0, marginLeft: "3px" }}>
            Itinerary List
          </Typography.h4>

          <div>
            {isAddAccess && (
              <Button
                themeColor={"primary"}
                fillMode={"solid"}
                style={{ marginRight: 10 }}
                onClick={() => handleOpenBulkUploadDialog()}
              >
                Bulk Upload
              </Button>
            )}
            {isAddAccess && (
              <Button
                onClick={handleCreate}
                fillMode={"solid"}
                themeColor={"primary"}
              >
                {"Add New"}
              </Button>
            )}
          </div>
        </GridLayoutItem>
        <GridLayoutItem>
          <ItineraryGridComponent />
        </GridLayoutItem>
      </GridLayout>
    </>
  );
};

interface PageState {
  skip: number;
  take: number;
}

const initialDataState: PageState = { skip: 0, take: 10 };

const ItineraryGridComponent: React.FC = () => {
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isEditAccess = checkAcessRights(location.pathname, EDIT_ACCESS);
  const isDeleteAccess = checkAcessRights(location.pathname, DELETE_ACCESS);
  const ItinearyList = useAppSelector((state) => state.itineary.ItinearyList);
  const loading = useAppSelector((state) => state.itineary.loading);

  const [filter, setFilter] = React.useState<
    CompositeFilterDescriptor | undefined
  >(undefined);
  const [page, setPage] = React.useState<PageState>(initialDataState);
  const [pageSizeValue, setPageSizeValue] = React.useState<
    number | string | undefined
  >();
  const [expandedRow, setExpandedRow] = useState(null);
  const truncateDescription = (Description: string) => {
    if (!Description) return "";
    const maxLength = 100;
    if (Description.length <= maxLength) return Description;
    return Description.substring(0, maxLength) + "...";
  };

  const pageChange = (event: GridPageChangeEvent) => {
    const targetEvent = event.targetEvent as PagerTargetEvent;
    const take =
      targetEvent.value === "All" ? ItinearyList.length : event.page.take;

    if (targetEvent.value) {
      setPageSizeValue(targetEvent.value);
    }
    setPage({
      ...event.page,
      take,
    });
  };

  const MyEditCommandCell = (props: GridCellProps) => {
    const handleEdit = (ID: number) => {
      navigate("/itineary/edit", { state: { ItinearyID: ID } });
    };
    const handleOpenDeleteDialog = (ID: number) => {
      dispatch(openDialog("deleteItinerary"));
      dispatch(setItinearyID(ID));
    };

    return (
      <td
        className="action-td"
        style={{
          ...props.style,
        }}
      >
        {isEditAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Edit"
            onClick={() => handleEdit(props.dataItem.ID)}
            icon={FiEdit}
            iconClassName="absolute-position"
            iconStyle={{ fontSize: "20px" }}
          />
        )}
        {isDeleteAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Delete"
            className="color-red"
            onClick={() => handleOpenDeleteDialog(props.dataItem.ID)}
            icon={MdDelete}
            iconClassName="absolute-position"
            iconStyle={{ fontSize: "24px" }}
          />
        )}
      </td>
    );
  };

  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setFilter(e.filter);
    setPage({ skip: 0, take: page.take });
  };
  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className="table-header"
    >
      {props.children}
    </HeaderThElement>
  );

  const CustomCell = (props: any) => {
    return (
      <td {...props.tdProps} colSpan={1} className={"table-cell"}>
        {props.children}
      </td>
    );
  };

  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <CustomCell {...props} />
  );

  const MyActiveInActtiveCommandCell = (props: GridCellProps) => {
    const [IsActive, setIsActive] = useState<boolean>(props.dataItem.IsActive);

    const handleActiveInActive = () => {
      setIsActive(!IsActive);
      const data = {
        ID: props.dataItem.ID,
        IsActive: !IsActive,
      };
      dispatch(ActiveInactiveItinerary(data)).then((response: any) => {
        if (response.payload?.data?.Status === 200) {
          dispatch(getAllItinerary());
        } else {
          dispatch(getAllItinerary());
        }
      });
    };

    return (
      <td style={{ textAlign: "center" }}>
        <Switch
          disabled={isEditAccess ? false : true}
          name="IsActive"
          checked={IsActive}
          onChange={handleActiveInActive}
          size="small"
        />
      </td>
    );
  };

  if (loading) return <LoadingPanel gridRef={gridRef} />;
  return (
    <>
      {ItinearyList && ItinearyList.length ? (
        <KendoGrid
          style={{ maxHeight: "calc(100vh - 152px)" }}
          className="responsive-table"
          filterable={true}
          filter={filter}
          data={
            filter
              ? filterBy(ItinearyList, filter).slice(
                  page.skip,
                  page.take + page.skip
                )
              : ItinearyList.slice(page.skip, page.take + page.skip)
          }
          onFilterChange={handleFilterChange}
          skip={page.skip}
          take={page.take}
          total={ItinearyList.length}
          pageable={{
            buttonCount: 5,
            pageSizes: [5, 10, 15, "All"],
            pageSizeValue: pageSizeValue,
            type: "input",
          }}
          onPageChange={pageChange}
          cells={{
            headerCell: HeaderCustomCell,
            data: MyDataCustomCell,
          }}
        >
          <Column
            field="ItineraryTitle"
            title="Title"
            cell={(props: any) => (
              <td className="hoverableName">
                {isEditAccess ? (
                  <div
                    className="hoverableName"
                    onClick={() =>
                      navigate("/itineary/edit", {
                        state: { ItinearyID: props.dataItem.ID },
                      })
                    }
                  >
                    {props.dataItem?.ItineraryTitle}
                  </div>
                ) : (
                  <div className="normalName">
                    {props.dataItem?.ItineraryTitle}
                  </div>
                )}
              </td>
            )}
          />

          <Column field="CityName" title="CityName" />
          {/* <Column field="Description" title="Description" /> */}

          <Column
            field="Description"
            title="Description"
            width={"350px"}
            cell={(props: any) => (
              <td>
                <div
                  className="remarks-content"
                  dangerouslySetInnerHTML={{
                    __html:
                      expandedRow === props.dataItem?.ID
                        ? props.dataItem?.Description
                        : truncateDescription(props.dataItem?.Description),
                  }}
                />
                {props.dataItem?.Description?.length > 100 && (
                  <span
                    className="readmore-btn"
                    style={{
                      textAlign: "right",
                    }}
                    onClick={() =>
                      setExpandedRow(
                        expandedRow === props.dataItem?.ID
                          ? null
                          : props.dataItem?.ID
                      )
                    }
                  >
                    <span>
                      {" "}
                      {expandedRow === props.dataItem?.ID
                        ? "Read Less"
                        : "Read More"}
                    </span>
                  </span>
                )}
              </td>
            )}
          />

          <Column
            field="ID"
            title="Status"
            cell={MyActiveInActtiveCommandCell}
            width={"90px"}
            filterable={false}
            locked={true}
          />
          {(isEditAccess || isDeleteAccess) && (
            <Column
              field="ID"
              title="Actions"
              cell={MyEditCommandCell}
              width={"110px"}
              filterable={false}
            />
          )}
        </KendoGrid>
      ) : (
        <AlertBox />
      )}
    </>
  );
};

const DeleteItineraryDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const ItinearyID = useAppSelector((state) => state.itineary.ItineraryID);
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const handleDeleteAction = async (ID: number | null) => {
    if (ID) {
      const response = await dispatch(deleteItinerary(ID));
      if (response.meta.requestStatus === "fulfilled") {
        dispatch(getAllItinerary());
        dispatch(closeDialog());
        dispatch(clearItinearyID());
      } else {
        dispatch(getAllItinerary());
        dispatch(closeDialog());
        dispatch(clearItinearyID());
      }
    }
  };

  return (
    <>
      {dialogName === "deleteItinerary" && (
        <AppDialog>
          <>
            <Typography.h5>{"Delete Itinerary"}</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this Itinerary ?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  display: "flex",
                  justifyContent: "end",
                }}
              >
                <DialogCloseButton themeColor="error" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={() => handleDeleteAction(ItinearyID)}
                  type="button"
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}
    </>
  );
};

const BulkUploadItineraryDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const loading = useAppSelector((state) => state.itineary.bulkUploadLoading);

  const handleSubmit = async (values: any) => {
    const formData = new FormData();
    formData.append("file", values?.ItineraryDocument[0]?.getRawFile());

    dispatch(bulkUploadItineraries(formData)).then((res: any) => {
      if (res?.payload?.Status === 200) {
        dispatch(closeDialog());
        dispatch(getAllItinerary());
      }
    });
  };

  return (
    <>
      {dialogName === "bulkUploadItinerary" && (
        <AppDialog>
          <>
            {loading && <LoadingPanel gridRef={gridRef} />}
            <Form
              onSubmit={handleSubmit}
              render={() => (
                <FormElement style={{ minWidth: 500 }}>
                  <GridLayout>
                    <GridLayoutItem className="position-relative">
                      <Typography.h4 className="typography-head">
                        {"Bulk Upload Itineraries"}
                      </Typography.h4>
                      <IconButton
                        className="typography-dialog-close-icon"
                        type="button"
                        fillMode={"flat"}
                        themeColor={"dark"}
                        style={{ marginRight: 4, top: 7 }}
                        onClick={() => dispatch(closeDialog())}
                        icon={RxCross1}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        name="ItineraryDocument"
                        type="file"
                        filetype="file"
                        label="Itinerary File"
                        accept=".xls, .xlsx, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                        multiple={false}
                        component={FormFileUpload}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginTop: 20,
                      }}
                    >
                      <Button
                        themeColor={"primary"}
                        fillMode={"solid"}
                        style={{ marginRight: 10 }}
                        onClick={() => window.open(itinerarySample)}
                      >
                        Show Sample
                      </Button>
                      <div>
                        <DialogCloseButton themeColor="primary" />
                        <ButtonWithLoading
                          label={"Upload"}
                          type="submit"
                          // disabled={!formRenderProps.allowSubmit || formLoading}
                          // loading={formLoading}
                        />
                      </div>
                    </GridLayoutItem>
                  </GridLayout>
                </FormElement>
              )}
            />
          </>
        </AppDialog>
      )}
    </>
  );
};

export default Itinerary;
